import {
  getActiveWorkspace,
  getColourizedImageUrl,
  getFrameIndexForPanel,
  getImagesForPanels,
  getNextId,
  getPanelProperties,
  getPanelsForActiveWorkspace,
  getTime,
  getFrameList,
  getFrameListsForPanel
} from "../selectors";
import {
  defaultImage,
  defaultFrame,
  defaultFrameList
} from "../selectors/default";
import { updateImagesSuccess } from "../actions/image";
import { randomCompositeKey } from "../util/panel";
import { generatePanelUrl } from "../network/image";
import { colourizeImage } from "../util/colourizer";
import { colourizedImageSetId } from "../reducers/image";

export const CREATE_PANEL = "CREATE_PANEL";
export const DELETE_PANEL = "DELETE_PANEL";
export const UPDATE_PANEL_FRAME_LIST = "UPDATE_PANEL_FRAME_LIST";
export const UPDATE_PANEL_FRAME = "UPDATE_PANEL_FRAME";
export const DELETE_PANEL_FRAME_ACTIVE_IMAGE =
  "DELETE_PANEL_FRAME_ACTIVE_IMAGE";
export const UPDATE_PANEL_IMAGE = "UPDATE_PANEL_IMAGE";
export const UPDATE_PANEL_MAXIMIZE = "UPDATE_PANEL_MAXIMIZE";
export const UPDATE_PANEL_POSITION = "UPDATE_PANEL_POSITION";
export const UPDATE_PANEL_SIZE = "UPDATE_PANEL_SIZE";
export const UPDATE_PANEL_ZINDEX = "UPDATE_PANEL_ZINDEX";
export const UPDATE_PANEL_PLAYBACK = "UPDATE_PANEL_PLAYBACK";
export const TOGGLE_PANEL_OPTIONS = "TOGGLE_PANEL_OPTIONS";
export const UPDATE_PANEL_PLAYBACK_SPEED = "UPDATE_PANEL_PLAYBACK_SPEED";
export const UPDATE_PANEL_LIVE_MODE = "UPDATE_PANEL_LIVE_MODE";
export const UPDATE_PANEL_CROP = "UPDATE_PANEL_CROP";
export const UPDATE_PANEL_TEMPERATURE_DISPLAY_ENABLED =
  "UPDATE_PANEL_TEMPERATURE_DISPLAY_ENABLED";
export const UPDATE_PANEL_ZOOM = "UPDATE_PANEL_ZOOM";
export const UPDATE_PANEL_COLOUR_CURVE = "UPDATE_PANEL_COLOUR_CURVE";
export const UPDATE_IMAGE_COLOURIZED_URL = "UPDATE_IMAGE_COLOURIZED_URL";

export function updateTemperatureDisplay(panelId, temperatureDisplayEnabled) {
  return {
    type: UPDATE_PANEL_TEMPERATURE_DISPLAY_ENABLED,
    panelId
  };
}

export function updatePanelNextFrame(panelId, fromPlayback) {
  return (dispatch, getState, networkManager) => {
    const { frameList, frameIndex } = getFrameIndexForPanel(
      getState(),
      panelId
    );
    // If we don't have a frameList, return without doing anything
    if (!frameList) {
      return;
    }
    const nextFrameIndex = (frameIndex + 1) % frameList.frames.length;
    const frame = frameList.frames[nextFrameIndex];

    // Pre-fetch the image for the next frame. When it returns successfully,
    // set that frame as active.
    networkManager.requests.getImage(
      panelId,
      data => {
        const panel = getState().panels.find(panel => panel.id === panelId);

        // This is to prevent a race condition during playback where live mode
        // is set before this prefetch finishes (especially noticable on slow
        // connections). If we're already in live mode, don't bother doing anything
        // if this function was called from playback. Otherwise, proceed as normal.
        if (!fromPlayback || (panel && !panel.isLiveMode)) {
          dispatch(updateFrame(panelId, frameList.id, frame.id, fromPlayback));
        }
      },
      {
        url: window.urls.image(defaultImage(frame.images).id)
      }
    );
  };
}

export function updatePanelPreviousFrame(panelId) {
  return (dispatch, getState, networkManager) => {
    const { frameList, frameIndex } = getFrameIndexForPanel(
      getState(),
      panelId
    );
    // If we don't have a frameList, return without doing anything
    if (!frameList) {
      return;
    }
    const previousFrameIndex =
      frameIndex === 0 ? frameList.frames.length - 1 : frameIndex - 1;
    const frame = frameList.frames[previousFrameIndex];
    dispatch(updateFrame(panelId, frameList.id, frame.id, false)); // fromPlayback is always false: playback is only forward
  };
}

export function updateImage(panelId, frameId, frameListId, imageId) {
  return (dispatch, getState, networkManager) => {
    const url = window.urls.image(imageId);
    networkManager.requests.getImage(
      panelId,
      d => {
        dispatch({
          type: UPDATE_PANEL_IMAGE,
          panelId,
          frameId,
          frameListId,
          imageId
        });
      },
      { url }
    );
  };
}

export function updateFrame(
  panelId,
  frameListId,
  frameId,
  fromPlayback = false
) {
  return (dispatch, getState, networkManager) => {
    // If we aren't updating the frame from playback

    if (fromPlayback !== true) {
      // Ensure playback is false
      dispatch(setPanelPlayback(panelId, false));
    }

    const panel = getState().panels.find(panel => panel.id === panelId);
    // If colour curve is enabled, we have a whole lotta work to do
    // before we can switch frames.
    const frameList = getFrameIndexForPanel(getState(), panelId).frameList;
    const frame = frameList.frames.find(frame => frame.id === frameId);

    if (frame === undefined) {
      return null;
    }
    const activeImageId = panel.userSelected.image[frame.id];
    const imgUrl = window.urls.image(
      activeImageId || defaultImage(frame.images).id
    );

    if (panel.colourCurve.selected) {
      const colourizedImageUrl = getColourizedImageUrl(
        colourizedImageSetId(panelId, panel.colourCurve.selected),
        imgUrl
      )(getState());

      // If we already have a colourized image for this image ID, then we can safely
      // go ahead and switch frames. However if we need to colourize it first, then
      // we need to force the panel to wait while we do so before switching frames.
      // Otherwise, the uncolourized image will likely be drawn first, to be replaced
      // by the colourized one milliseconds later, resulting in an annoying flicker.
      // This is especially bad / noticeable in playback.
      if (!colourizedImageUrl) {
        const colourMap =
          window.settings.workspace.colourCurves[panel.colourCurve.selected]
            .map;
        colourizeImage(colourMap, imgUrl, colourizedImageUrl => {
          dispatch({
            type: UPDATE_IMAGE_COLOURIZED_URL,
            key: colourizedImageSetId(panelId, panel.colourCurve.selected),
            imgUrl,
            colourizedImageUrl
          });
          dispatch({
            type: UPDATE_PANEL_FRAME,
            panelId,
            frameListId,
            frameId
          });
        });
      } else {
        dispatch({
          type: UPDATE_PANEL_FRAME,
          panelId,
          frameListId,
          frameId
        });
      }
    } else {
      networkManager.requests.getImage(
        panelId,
        d => {
          dispatch({
            type: UPDATE_PANEL_FRAME,
            panelId,
            frameListId,
            frameId
          });
        },
        { url: imgUrl }
      );
    }
  };
}

export function updateFrameList(panelId, frameListId) {
  return (dispatch, getState, networkManager) => {
    // Ensure playback is stopped
    dispatch(setPanelPlayback(panelId, false));

    const state = getState();
    let frameList = getFrameList(state, frameListId);

    // If the frameList is null, it didn't exist. This is the case when a framelist expires.
    // Fallback to the default framelist.
    if (frameList === null) {
      frameList = defaultFrameList(
        getTime(state),
        getFrameListsForPanel(state, panelId)
      );
    }
    const frame = defaultFrame(getTime(state), frameList.frames);
    const image = defaultImage(frame.images);
    const url = window.urls.image(image.id);

    networkManager.requests.getImage(
      panelId,
      d => {
        dispatch({
          type: UPDATE_PANEL_FRAME_LIST,
          panelId,
          frameListId
        });
      },
      { url }
    );
  };
}

export function updatePosition(panelId, position) {
  return {
    type: UPDATE_PANEL_POSITION,
    id: panelId,
    position: position
  };
}

export function updateCrop(panelId, crop) {
  return {
    type: UPDATE_PANEL_CROP,
    id: panelId,
    crop: crop
  };
}

export function updateZoom(panelId, zoom) {
  return {
    type: UPDATE_PANEL_ZOOM,
    id: panelId,
    zoom: zoom
  };
}

export function updateSize(panelId, direction, sizeChange) {
  return {
    type: UPDATE_PANEL_SIZE,
    id: panelId,
    sizeChange: sizeChange,
    direction: direction
  };
}

export function toggleMaximize(panelId, state) {
  return {
    type: UPDATE_PANEL_MAXIMIZE,
    id: panelId,
    state
  };
}

export function closePanel(panelId) {
  return {
    type: DELETE_PANEL,
    id: panelId
  };
}

// Return a thunk for creating the panel
// this will allow it to read the active workspace and nextId
// from the store so we can associate the new panel with the active workspace
export function createPanel({ compositeKey } = {}) {
  if (compositeKey === undefined) {
    compositeKey = randomCompositeKey();
  }

  return (dispatch, getState, networkManager) => {
    let state = getState();

    let workspace = getActiveWorkspace(state);

    dispatch({
      type: CREATE_PANEL,
      panelId: getNextId(state),
      workspaceId: workspace.id,
      compositeKey: compositeKey,
      zIndex: workspace.zIndex,
      numberOfPanels: getPanelsForActiveWorkspace(state).length + 1 // include the new panel in the total count
    });

    // Since we have added a new panel to our workspace, need to request some data and dispatch an action when it's done
    var images = getImagesForPanels(getState());

    networkManager.requests.getFrameLists(
      data => {
        dispatch(updateImagesSuccess(data));
      },
      {
        url: generatePanelUrl(images)
      }
    );
  };
}

// Touching (onMouseDown) a panel should immediately
// update it's z-index to highest value within the workspace
export function touchPanel(panelId, evnt) {
  return (dispatch, getState) => {
    const state = getState();
    const workspace = getActiveWorkspace(state);
    dispatch({
      type: UPDATE_PANEL_ZINDEX,
      zIndex: workspace.zIndex,
      panelId,
      workspaceId: workspace.id,
      numberOfPanels: getPanelsForActiveWorkspace(state).length
    });
  };
}

export function setPanelLiveMode(panelId, isLiveMode = true) {
  return {
    type: UPDATE_PANEL_LIVE_MODE,
    panelId,
    isLiveMode
  };
}

export function setPanelPlayback(panelId, value = null) {
  return (dispatch, getState) => {
    // Set live mode to false when playback starts.
    // This also occurs when playback is paused / stops, however
    // we still keep live mode to false, since that requires an
    // explicit user choice. Since that value remains unchanged,
    // nothing should happen to the state as a result of subsequent
    // calls to this action.
    dispatch(setPanelLiveMode(panelId, false));
    const frameList = getFrameIndexForPanel(getState(), panelId).frameList;
    // If we don't have a frameList, return without doing anything
    if (!frameList) {
      return;
    }
    dispatch({
      type: UPDATE_PANEL_PLAYBACK,
      panelId,
      value
    });
  };
}

export function togglePanelOptions(panelId) {
  return {
    type: TOGGLE_PANEL_OPTIONS,
    panelId
  };
}

export function updatePanelPlaybackSpeed(panelId, playbackSpeedModifier) {
  return {
    type: UPDATE_PANEL_PLAYBACK_SPEED,
    panelId,
    playbackSpeedModifier
  };
}

export function updateColourCurve(panelId, activeImageId, colourCurveId) {
  const colourCurve = window.settings.workspace.colourCurves[colourCurveId];
  // If the specified colourCurve is not valid; set to None
  if (!colourCurve) {
    colourCurveId = 0;
  }
  return (dispatch, getState) => {
    const state = getState();
    const panelMetaData = getPanelProperties(panelId)(state);

    const updatePanelColourCurveAction = {
      type: UPDATE_PANEL_COLOUR_CURVE,
      panelId,
      from: panelMetaData.colourCurve.selected,
      to: colourCurveId
    };

    if (colourCurveId > 0) {
      var imgUrl = window.urls.image(activeImageId);
      var colourizedImageUrl = getColourizedImageUrl(
        colourizedImageSetId(panelId, colourCurveId),
        imgUrl
      )(state);
      if (!colourizedImageUrl) {
        const colourMap = colourCurve.map;
        // Start colouring the image when colour curves become enabled.
        colourizeImage(colourMap, imgUrl, colourizedImageUrl => {
          dispatch(updatePanelColourCurveAction);
          dispatch({
            type: UPDATE_IMAGE_COLOURIZED_URL,
            key: colourizedImageSetId(panelId, updatePanelColourCurveAction.to),
            imgUrl,
            colourizedImageUrl
          });
        });
      } else {
        dispatch(updatePanelColourCurveAction);
        dispatch({
          type: UPDATE_IMAGE_COLOURIZED_URL,
          key: colourizedImageSetId(panelId, updatePanelColourCurveAction.to),
          imgUrl,
          colourizedImageUrl
        });
      }
    } else {
      dispatch(updatePanelColourCurveAction);
    }
  };
}
